import { lazy, Suspense, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  createBridgePrompt,
  utcDayPeriod,
  type BridgePrompt,
  type RuntimeDiscoveryDataset,
  type RuntimeGraphManifest,
} from '@entangled-vega/shared';

import { AtlasSearchControls } from './components/AtlasSearchControls.js';
import { ChallengePage } from './components/ChallengePage.js';
import type { FocusRequest } from './components/ConstellationView.js';
import { ExplorationTrail } from './components/ExplorationTrail.js';
import type { StepGuessRange } from './components/FeaturedGuess.js';
import { FeaturedDiscoveries } from './components/FeaturedDiscoveries.js';
import { LensControl } from './components/LensControl.js';
import { ListView } from './components/ListView.js';
import { NextTemptations } from './components/NextTemptations.js';
import { PersonPanel } from './components/PersonPanel.js';
import { SavedLibrary } from './components/SavedLibrary.js';
import { SiteHeader } from './components/SiteHeader.js';
import { AboutPage, NotFoundPage, PrivacyPage, WorkWithUsPage } from './components/StaticPages.js';
import { TraceBanner } from './components/TraceBanner.js';
import {
  RELATIONSHIP_LENSES,
  type GraphDataset,
  type GraphPerson,
  type RelationshipLens,
  type SearchPerson,
} from './data/graph-types.js';
import {
  loadProductionDiscovery,
  loadProductionGraph,
  loadProductionManifest,
} from './data/runtime-loader.js';
import {
  bestDocumentedPath,
  createGraphIndex,
  featuredTracePath,
  graphDatasetForLenses,
} from './lib/graph.js';
import {
  defaultDossierSheetState,
  initialDossierWorkspaceForView,
  type DossierWorkspaceState,
} from './lib/dossier-workspace.js';
import {
  DEVICE_LIBRARY_KEY,
  createBrowserDeviceLibraryController,
  traceReferenceKey,
  type DeviceLibraryController,
  type DeviceLibrarySnapshot,
  type StoredTraceReference,
} from './lib/device-library.js';
import {
  createExplorationTrailState,
  explorationTrailJumpTarget,
  explorationTrailReducer,
} from './lib/exploration-trail.js';
import { createNoPathRecovery } from './lib/no-path-recovery.js';
import { createCompletedPathCard, downloadCompletedPathCard } from './lib/path-card.js';
import {
  RELATIONSHIP_FILTERS,
  type FocusDepth,
  type RelationshipFilter,
} from './lib/relationship-view.js';
import { CANONICAL_SITE_ORIGIN, traceShareDetails, type TraceShareDetails } from './lib/sharing.js';
import {
  createNextTemptations,
  createPersonSummaryModel,
  findAlternativeDocumentedPaths,
  type NextTemptation,
} from './lib/story-graph-models.js';
import { storyPathKey } from './lib/story-playback.js';
import {
  DEFAULT_RELATIONSHIP_LENSES,
  invalidAtlasUrlParameters,
  normalizeAtlasUrlState,
  parseAtlasUrlState,
  serializeAtlasUrlState,
  transitionAtlasUrlState,
  type AtlasUrlState,
} from './lib/url-state.js';

const ConstellationView = lazy(async () => {
  const module = await import('./components/ConstellationView.js');
  return { default: module.ConstellationView };
});

function pagePath(): string {
  return window.location.pathname.replace(/\/+$/u, '') || '/';
}

function invalidLinkDescription(issues: readonly string[]): string {
  const labels: Readonly<Record<string, string>> = {
    person: 'selected person',
    from: 'starting person',
    to: 'ending person',
    lenses: 'relationship lens',
    story: 'featured story',
  };
  return issues.map((issue) => labels[issue] ?? issue).join(', ');
}

async function copyText(text: string): Promise<void> {
  const clipboard = Reflect.get(navigator, 'clipboard') as
    { writeText?: (value: string) => Promise<void> } | undefined;
  if (typeof clipboard?.writeText === 'function') {
    await clipboard.writeText(text);
    return;
  }
  const input = document.createElement('textarea');
  input.value = text;
  input.readOnly = true;
  input.style.position = 'fixed';
  input.style.opacity = '0';
  document.body.append(input);
  try {
    input.select();
    if (!document.execCommand('copy')) {
      throw new Error('The browser did not copy the trace URL.');
    }
  } finally {
    input.remove();
  }
}

function AtlasStatus(props: { error?: string; onRetry?: () => void }) {
  useEffect(() => {
    performance.mark('entangled-vega:shell-ready');
  }, []);
  return (
    <div className="app-shell">
      <SiteHeader badge={props.error === undefined ? 'Loading data' : 'Data unavailable'} />
      <main id="main-content" className="atlas">
        <div className="atlas__stage">
          <div className="constellation constellation--loading" role="status">
            <div className="data-status">
              <p>{props.error ?? 'Mapping the relationship constellation…'}</p>
              {props.onRetry === undefined ? null : (
                <button className="button button--gold" type="button" onClick={props.onRetry}>
                  Retry data load
                </button>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

type ResourceState<Value> =
  { status: 'loading' } | { status: 'ready'; value: Value } | { status: 'error'; message: string };

function AtlasDataBoundary() {
  const [manifestAttempt, setManifestAttempt] = useState(0);
  const [discoveryAttempt, setDiscoveryAttempt] = useState(0);
  const [graphAttempt, setGraphAttempt] = useState(0);
  const [manifestState, setManifestState] = useState<ResourceState<RuntimeGraphManifest>>({
    status: 'loading',
  });
  const [discoveryState, setDiscoveryState] = useState<ResourceState<RuntimeDiscoveryDataset>>({
    status: 'loading',
  });
  const [graphState, setGraphState] = useState<ResourceState<GraphDataset>>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    setManifestState({ status: 'loading' });
    setDiscoveryState({ status: 'loading' });
    setGraphState({ status: 'loading' });
    void loadProductionManifest({ signal: controller.signal }).then(
      (manifest) => {
        if (!controller.signal.aborted) {
          setManifestState({ status: 'ready', value: manifest });
        }
      },
      (error: unknown) => {
        if (!controller.signal.aborted) {
          setManifestState({
            status: 'error',
            message:
              error instanceof Error
                ? `The current data manifest could not be loaded: ${error.message}`
                : 'The current data manifest could not be loaded.',
          });
        }
      },
    );
    return () => controller.abort();
  }, [manifestAttempt]);

  useEffect(() => {
    if (manifestState.status !== 'ready') return;
    const controller = new AbortController();
    setDiscoveryState({ status: 'loading' });
    void loadProductionDiscovery({
      manifest: manifestState.value,
      signal: controller.signal,
    }).then(
      (discovery) => {
        if (!controller.signal.aborted) {
          setDiscoveryState({ status: 'ready', value: discovery });
        }
      },
      (error: unknown) => {
        if (!controller.signal.aborted) {
          setDiscoveryState({
            status: 'error',
            message:
              error instanceof Error
                ? `Search data could not be loaded: ${error.message}`
                : 'Search data could not be loaded.',
          });
        }
      },
    );
    return () => controller.abort();
  }, [discoveryAttempt, manifestState]);

  useEffect(() => {
    if (manifestState.status !== 'ready') return;
    const controller = new AbortController();
    setGraphState({ status: 'loading' });
    void loadProductionGraph({ manifest: manifestState.value, signal: controller.signal }).then(
      (dataset) => {
        if (!controller.signal.aborted) {
          setGraphState({ status: 'ready', value: dataset });
        }
      },
      (error: unknown) => {
        if (!controller.signal.aborted) {
          setGraphState({
            status: 'error',
            message:
              error instanceof Error
                ? `The complete constellation could not be loaded: ${error.message}`
                : 'The complete constellation could not be loaded.',
          });
        }
      },
    );
    return () => controller.abort();
  }, [graphAttempt, manifestState]);

  if (manifestState.status === 'loading') return <AtlasStatus />;
  if (manifestState.status === 'error') {
    return (
      <AtlasStatus
        error={manifestState.message}
        onRetry={() => setManifestAttempt((value) => value + 1)}
      />
    );
  }

  const discovery = discoveryState.status === 'ready' ? discoveryState.value : null;
  const dataset = graphState.status === 'ready' ? graphState.value : null;
  if (discovery === null && dataset === null) {
    if (discoveryState.status === 'error' && graphState.status === 'error') {
      return (
        <AtlasStatus
          error={`${discoveryState.message} ${graphState.message}`}
          onRetry={() => {
            setDiscoveryAttempt((value) => value + 1);
            setGraphAttempt((value) => value + 1);
          }}
        />
      );
    }
    return <AtlasStatus />;
  }

  return (
    <AtlasApplication
      manifest={manifestState.value}
      discovery={discovery}
      dataset={dataset}
      graphState={graphState}
      onRetryGraph={() => setGraphAttempt((value) => value + 1)}
    />
  );
}

function AtlasApplication({
  manifest,
  discovery,
  dataset,
  graphState,
  onRetryGraph,
}: {
  manifest: RuntimeGraphManifest;
  discovery: RuntimeDiscoveryDataset | null;
  dataset: GraphDataset | null;
  graphState: ResourceState<GraphDataset>;
  onRetryGraph: () => void;
}) {
  const activePeople = useMemo<readonly SearchPerson[]>(
    () => dataset?.people ?? discovery?.people ?? [],
    [dataset, discovery],
  );
  useEffect(() => {
    performance.mark('entangled-vega:search-ready');
  }, []);
  const validIds = useMemo(() => new Set(activePeople.map((person) => person.id)), [activePeople]);
  const validStories = useMemo(
    () =>
      new Map(
        (dataset?.featuredTraces ?? discovery?.featuredTraces ?? []).map((trace) => [
          trace.id,
          trace,
        ]),
      ),
    [dataset, discovery],
  );
  const [storedAtlasState, setAtlasState] = useState<AtlasUrlState>(() =>
    parseAtlasUrlState(window.location.search, validIds, validStories),
  );
  const [urlIssues, setUrlIssues] = useState<readonly string[]>(() =>
    invalidAtlasUrlParameters(window.location.search, validIds, validStories),
  );
  const [focusRequest, setFocusRequest] = useState<FocusRequest | null>(null);
  const [resetToken, setResetToken] = useState(0);
  const [snapshotRequestToken, setSnapshotRequestToken] = useState(0);
  const [selectionHistory, setSelectionHistory] = useState<readonly string[]>([]);
  const [focusDepth, setFocusDepth] = useState<FocusDepth>('direct');
  const [relationshipFilters, setRelationshipFilters] =
    useState<readonly RelationshipFilter[]>(RELATIONSHIP_FILTERS);
  const [dossierWorkspace, setDossierWorkspace] = useState<DossierWorkspaceState>(() =>
    initialDossierWorkspaceForView(storedAtlasState.view),
  );
  const previousDossierRouteRef = useRef({
    personId: storedAtlasState.personId,
    view: storedAtlasState.view,
  });
  const [shareFeedback, setShareFeedback] = useState<string | null>(null);
  const [graphicsNotice, setGraphicsNotice] = useState(false);
  const [exampleIndex, setExampleIndex] = useState(0);
  const [selectedRouteKey, setSelectedRouteKey] = useState<string | null>(null);
  const [featuredGuess, setFeaturedGuess] = useState<{
    storyKey: string;
    guess: StepGuessRange | null;
    bridgeChoiceId: string | null;
    revealed: boolean;
  } | null>(null);
  const [trail, dispatchTrail] = useReducer(explorationTrailReducer, createExplorationTrailState());
  const [controlsExpanded, setControlsExpanded] = useState(
    () =>
      storedAtlasState.personId === null &&
      storedAtlasState.traceFrom === null &&
      storedAtlasState.traceTo === null,
  );
  const activePeopleById = useMemo(
    () => new Map(activePeople.map((person) => [person.id, person])),
    [activePeople],
  );
  const visibleDataset = useMemo(
    () => (dataset === null ? null : graphDatasetForLenses(dataset, storedAtlasState.lenses)),
    [dataset, storedAtlasState.lenses],
  );
  const baseIndex = useMemo(() => (dataset === null ? null : createGraphIndex(dataset)), [dataset]);
  useEffect(() => {
    const previous = previousDossierRouteRef.current;
    previousDossierRouteRef.current = {
      personId: storedAtlasState.personId,
      view: storedAtlasState.view,
    };
    if (storedAtlasState.personId !== null && storedAtlasState.personId !== previous.personId) {
      setDossierWorkspace((current) => defaultDossierSheetState(current, 'half'));
      return;
    }
    if (storedAtlasState.view !== previous.view) {
      setDossierWorkspace((current) =>
        defaultDossierSheetState(current, storedAtlasState.view === 'list' ? 'peek' : 'half'),
      );
    }
  }, [storedAtlasState.personId, storedAtlasState.view]);
  const index = useMemo(
    () =>
      visibleDataset === null
        ? null
        : visibleDataset === dataset
          ? baseIndex
          : createGraphIndex(visibleDataset),
    [baseIndex, dataset, visibleDataset],
  );
  const allLensesIndex = baseIndex;
  const libraryValidation = useMemo(() => {
    if (dataset === null || allLensesIndex === null) return null;
    const indexesByLenses = new Map([['personal,family', allLensesIndex]]);
    const indexForLenses = (lenses: readonly RelationshipLens[]) => {
      const key = lenses.join(',');
      const existing = indexesByLenses.get(key);
      if (existing !== undefined) return existing;
      const created = createGraphIndex(graphDatasetForLenses(dataset, lenses));
      indexesByLenses.set(key, created);
      return created;
    };
    const storyById = new Map((dataset.featuredTraces ?? []).map((trace) => [trace.id, trace]));
    return {
      datasetVersion: manifest.datasetVersion,
      validPersonIds: new Set(dataset.people.map((person) => person.id)),
      validStories: storyById,
      isValidTrace(trace: StoredTraceReference): boolean {
        const lenses = trace.lenses ?? DEFAULT_RELATIONSHIP_LENSES;
        const traceIndex = indexForLenses(lenses);
        if (trace.storyId !== undefined) {
          const story = storyById.get(trace.storyId);
          return story !== undefined && featuredTracePath(traceIndex, story) !== null;
        }
        return bestDocumentedPath(traceIndex, trace.from, trace.to) !== null;
      },
    };
  }, [allLensesIndex, dataset, manifest.datasetVersion]);
  const [libraryController, setLibraryController] = useState<DeviceLibraryController | null>(null);
  const [librarySnapshot, setLibrarySnapshot] = useState<DeviceLibrarySnapshot | null>(null);
  useEffect(() => {
    if (libraryValidation === null) {
      setLibraryController(null);
      setLibrarySnapshot(null);
      return;
    }
    const controller = createBrowserDeviceLibraryController(libraryValidation);
    setLibraryController(controller);
    setLibrarySnapshot(controller.snapshot());
  }, [libraryValidation]);
  useEffect(() => {
    if (libraryController === null) return;
    const reload = (event: StorageEvent) => {
      if (event.key === null || event.key === DEVICE_LIBRARY_KEY) {
        setLibrarySnapshot(libraryController.reload());
      }
    };
    window.addEventListener('storage', reload);
    return () => window.removeEventListener('storage', reload);
  }, [libraryController]);
  const personSummaryModel = useMemo(
    () => (index === null ? null : createPersonSummaryModel(index)),
    [index],
  );
  const lensCounts = useMemo(() => {
    if (dataset === null && discovery !== null) {
      return { ...discovery.relationshipCounts };
    }
    const counts: Record<RelationshipLens, number> = { personal: 0, family: 0 };
    for (const relationship of dataset?.relationships ?? []) {
      counts[relationship.lens] += 1;
    }
    return counts;
  }, [dataset, discovery]);

  useEffect(() => {
    const handlePopState = () => {
      const nextState = parseAtlasUrlState(window.location.search, validIds, validStories);
      setAtlasState(nextState);
      setUrlIssues(invalidAtlasUrlParameters(window.location.search, validIds, validStories));
      setSelectionHistory((current) =>
        current.at(-1) === nextState.personId ? current.slice(0, -1) : [],
      );
      setFocusDepth('direct');
      setRelationshipFilters(RELATIONSHIP_FILTERS);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [validIds, validStories]);

  useEffect(() => {
    setShareFeedback(null);
  }, [
    storedAtlasState.lenses,
    storedAtlasState.storyId,
    storedAtlasState.traceFrom,
    storedAtlasState.traceTo,
  ]);
  const activeStory =
    storedAtlasState.storyId === null ? undefined : validStories.get(storedAtlasState.storyId);
  const activeStoryPath = useMemo(() => {
    if (
      index !== null &&
      activeStory !== undefined &&
      activeStory.source === storedAtlasState.traceFrom &&
      activeStory.target === storedAtlasState.traceTo
    ) {
      return featuredTracePath(index, activeStory);
    }
    return null;
  }, [activeStory, index, storedAtlasState.traceFrom, storedAtlasState.traceTo]);
  const storedPath = useMemo(() => {
    if (
      index === null ||
      storedAtlasState.traceFrom === null ||
      storedAtlasState.traceTo === null
    ) {
      return null;
    }
    if (activeStoryPath !== null) {
      return activeStoryPath;
    }
    return bestDocumentedPath(index, storedAtlasState.traceFrom, storedAtlasState.traceTo);
  }, [activeStoryPath, index, storedAtlasState.traceFrom, storedAtlasState.traceTo]);
  const atlasState = useMemo(
    () =>
      normalizeAtlasUrlState(
        storedAtlasState,
        index === null ? {} : { tracePeople: storedPath?.people ?? null },
      ),
    [index, storedAtlasState, storedPath],
  );
  const path =
    atlasState.traceFrom === storedAtlasState.traceFrom &&
    atlasState.traceTo === storedAtlasState.traceTo
      ? storedPath
      : null;
  const alternativePaths = useMemo(
    () =>
      index === null ||
      path === null ||
      atlasState.traceFrom === null ||
      atlasState.traceTo === null
        ? []
        : findAlternativeDocumentedPaths({
            index,
            source: atlasState.traceFrom,
            target: atlasState.traceTo,
            primaryPath: path,
          }),
    [atlasState.traceFrom, atlasState.traceTo, index, path],
  );
  const selectedPath = useMemo(() => {
    if (path === null || selectedRouteKey === null) {
      return path;
    }
    return (
      [path, ...alternativePaths.map((alternative) => alternative.path)].find(
        (candidate) => storyPathKey(candidate) === selectedRouteKey,
      ) ?? path
    );
  }, [alternativePaths, path, selectedRouteKey]);
  const activeStoryKey =
    activeStoryPath === null || activeStory === undefined
      ? null
      : `${activeStory.id}:${storyPathKey(activeStoryPath) ?? 'invalid'}`;
  const activeFeaturedGuess =
    activeStoryKey !== null && featuredGuess?.storyKey === activeStoryKey ? featuredGuess : null;
  const storyConcealed = activeStoryPath !== null && activeFeaturedGuess?.revealed !== true;
  const displayPath = storyConcealed ? null : selectedPath;
  const selectedPathIsFeatured =
    activeStoryPath !== null && storyPathKey(selectedPath) === storyPathKey(activeStoryPath);
  const intentActive =
    atlasState.personId !== null || atlasState.traceFrom !== null || atlasState.traceTo !== null;
  useEffect(() => {
    if (!intentActive) {
      setControlsExpanded(true);
    }
  }, [intentActive]);
  const commit = (changes: Partial<AtlasUrlState>, replace = false) => {
    const nextState = transitionAtlasUrlState(
      atlasState,
      changes,
      index === null ? {} : { tracePeople: path?.people ?? null },
    );
    setAtlasState(nextState);
    setUrlIssues([]);
    const nextUrl = `/${serializeAtlasUrlState(nextState)}`;
    if (replace) {
      window.history.replaceState(null, '', nextUrl);
    } else {
      window.history.pushState(null, '', nextUrl);
    }
  };
  useEffect(() => {
    const canonicalSearch = serializeAtlasUrlState(atlasState);
    if (serializeAtlasUrlState(storedAtlasState) !== canonicalSearch) {
      setAtlasState(atlasState);
    }
    if (window.location.search !== canonicalSearch) {
      window.history.replaceState(null, '', `/${canonicalSearch}`);
    }
  }, [atlasState, storedAtlasState]);
  const visibleUrlIssues =
    index !== null &&
    atlasState.storyId !== null &&
    activeStoryPath === null &&
    !urlIssues.includes('story')
      ? [...urlIssues, 'story']
      : urlIssues;
  const selectedSearchPerson =
    atlasState.personId === null ? undefined : activePeopleById.get(atlasState.personId);
  const selectedPerson =
    atlasState.personId === null || index === null
      ? undefined
      : index.peopleById.get(atlasState.personId);
  const fromPerson =
    atlasState.traceFrom === null ? null : (activePeopleById.get(atlasState.traceFrom) ?? null);
  const toPerson =
    atlasState.traceTo === null ? null : (activePeopleById.get(atlasState.traceTo) ?? null);
  const noPathRecovery = useMemo(
    () =>
      index !== null &&
      allLensesIndex !== null &&
      atlasState.traceFrom !== null &&
      atlasState.traceTo !== null &&
      path === null
        ? createNoPathRecovery({
            activeIndex: index,
            allLensesIndex,
            from: atlasState.traceFrom,
            to: atlasState.traceTo,
            enabledLenses: atlasState.lenses,
          })
        : null,
    [allLensesIndex, atlasState.lenses, atlasState.traceFrom, atlasState.traceTo, index, path],
  );
  const suggestedTraces = useMemo(() => {
    if (dataset === null || index === null) {
      return discovery?.featuredTraces ?? [];
    }
    return (dataset.featuredTraces ?? [])
      .map((trace) => ({ trace, path: featuredTracePath(index, trace) }))
      .filter(
        (
          candidate,
        ): candidate is {
          trace: NonNullable<GraphDataset['featuredTraces']>[number];
          path: NonNullable<ReturnType<typeof bestDocumentedPath>>;
        } => candidate.path !== null && candidate.path.relationships.length >= 2,
      )
      .map((candidate) => candidate.trace);
  }, [dataset, discovery, index]);
  const featuredBridgePrompt = useMemo<BridgePrompt | null>(() => {
    if (activeStory === undefined || activeStoryPath === null || index === null) {
      return null;
    }
    const portfolio = suggestedTraces.flatMap((trace) => {
      const tracePath = featuredTracePath(index, trace);
      if (tracePath === null) return [];
      return [
        {
          id: trace.id,
          source: trace.source,
          target: trace.target,
          steps: tracePath.relationships.length,
          eligible: true,
          people: tracePath.people.map((personId) => {
            const person = index.peopleById.get(personId);
            return {
              id: personId,
              name: person?.name ?? personId,
              eligible: person?.inclusionRole !== 'context',
            };
          }),
        },
      ];
    });
    const candidate = portfolio.find(({ id }) => id === activeStory.id);
    if (candidate === undefined) return null;
    try {
      return createBridgePrompt({
        datasetVersion: manifest.datasetVersion,
        candidate,
        portfolio,
        period: utcDayPeriod(Date.now()),
      });
    } catch {
      return null;
    }
  }, [activeStory, activeStoryPath, index, manifest.datasetVersion, suggestedTraces]);
  const starterPeople = useMemo(() => {
    if (discovery !== null) {
      return discovery.starterPersonIds
        .map((personId) => activePeopleById.get(personId))
        .filter((person): person is SearchPerson => person !== undefined);
    }
    const seen = new Set<string>();
    const people: SearchPerson[] = [];
    for (const trace of suggestedTraces) {
      for (const personId of [trace.source, trace.target]) {
        const person = activePeopleById.get(personId);
        if (person !== undefined && person.inclusionRole !== 'context' && !seen.has(person.id)) {
          seen.add(person.id);
          people.push(person);
        }
        if (people.length >= 8) {
          return people;
        }
      }
    }
    return people;
  }, [activePeopleById, discovery, suggestedTraces]);
  const surprise = () => {
    const trace = suggestedTraces[exampleIndex % suggestedTraces.length];
    if (trace === undefined) {
      return;
    }
    setExampleIndex((current) => current + 1);
    setSelectionHistory([]);
    setFocusDepth('direct');
    setRelationshipFilters(RELATIONSHIP_FILTERS);
    setControlsExpanded(false);
    setSelectedRouteKey(null);
    setFeaturedGuess(null);
    commit({
      personId: null,
      traceFrom: trace.source,
      traceTo: trace.target,
      storyId: trace.id,
      view: 'graph',
    });
  };
  const shareDetails = useMemo<TraceShareDetails | null>(() => {
    if (
      index === null ||
      atlasState.traceFrom === null ||
      atlasState.traceTo === null ||
      selectedPath === null ||
      storyConcealed
    ) {
      return null;
    }
    return traceShareDetails(
      CANONICAL_SITE_ORIGIN,
      atlasState.traceFrom,
      atlasState.traceTo,
      atlasState.lenses,
      selectedPath,
      index,
      selectedPathIsFeatured ? atlasState.storyId : null,
    );
  }, [
    atlasState.lenses,
    atlasState.storyId,
    atlasState.traceFrom,
    atlasState.traceTo,
    index,
    selectedPath,
    selectedPathIsFeatured,
    storyConcealed,
  ]);
  const currentTraceReference = useMemo<StoredTraceReference | null>(() => {
    if (
      atlasState.traceFrom === null ||
      atlasState.traceTo === null ||
      selectedPath === null ||
      storyConcealed
    ) {
      return null;
    }
    const usesDefaultLenses =
      atlasState.lenses.length === DEFAULT_RELATIONSHIP_LENSES.length &&
      DEFAULT_RELATIONSHIP_LENSES.every((lens) => atlasState.lenses.includes(lens));
    return {
      from: atlasState.traceFrom,
      to: atlasState.traceTo,
      ...(usesDefaultLenses ? {} : { lenses: atlasState.lenses }),
      ...(selectedPathIsFeatured && atlasState.storyId !== null
        ? { storyId: atlasState.storyId }
        : {}),
    };
  }, [
    atlasState.lenses,
    atlasState.storyId,
    atlasState.traceFrom,
    atlasState.traceTo,
    selectedPath,
    selectedPathIsFeatured,
    storyConcealed,
  ]);
  const selectedSummary =
    selectedPerson === undefined ? null : (personSummaryModel?.get(selectedPerson.id) ?? null);
  const selectedPathKey = storyPathKey(displayPath);
  const selectedPersonFavorite =
    selectedPerson !== undefined &&
    librarySnapshot?.state.favoritePeople.includes(selectedPerson.id) === true;
  const currentTraceFavorite =
    currentTraceReference !== null &&
    librarySnapshot?.state.favoriteTraces.some(
      (trace) => traceReferenceKey(trace) === traceReferenceKey(currentTraceReference),
    ) === true;

  useEffect(() => {
    if (selectedPerson !== undefined) {
      dispatchTrail({
        type: 'visit-person',
        personId: selectedPerson.id,
        displayLabel: selectedPerson.name,
      });
    }
  }, [selectedPerson?.id, selectedPerson?.name]);

  useEffect(() => {
    if (selectedPerson !== undefined && libraryController !== null) {
      setLibrarySnapshot(libraryController.addRecentPerson(selectedPerson.id));
    }
  }, [libraryController, selectedPerson?.id]);

  useEffect(() => {
    if (displayPath !== null && fromPerson !== null && toPerson !== null) {
      dispatchTrail({
        type: 'complete-path',
        path: displayPath,
        sourceLabel: fromPerson.name,
        targetLabel: toPerson.name,
      });
    }
  }, [displayPath, fromPerson?.name, selectedPathKey, toPerson?.name]);

  const visitedPersonIds = useMemo(() => {
    const visited = new Set<string>();
    for (const entry of trail.entries) {
      if (entry.kind === 'person') {
        visited.add(entry.personId);
      } else {
        for (const personId of entry.peopleIds) {
          visited.add(personId);
        }
      }
    }
    return visited;
  }, [trail.entries]);
  const temptationPersonId = selectedPerson?.id ?? displayPath?.people.at(-1) ?? null;
  const nextTemptations = useMemo(
    () =>
      index === null || temptationPersonId === null
        ? []
        : createNextTemptations({
            index,
            personId: temptationPersonId,
            visitedPersonIds,
            excludedPersonIds: [
              ...(atlasState.traceFrom === null ? [] : [atlasState.traceFrom]),
              ...(atlasState.traceTo === null ? [] : [atlasState.traceTo]),
            ],
          }),
    [atlasState.traceFrom, atlasState.traceTo, index, temptationPersonId, visitedPersonIds],
  );
  const temptationPersonName =
    temptationPersonId === null
      ? 'This person'
      : (index?.peopleById.get(temptationPersonId)?.name ?? temptationPersonId);
  const selectPerson = (personId: string) => {
    if (personId === atlasState.personId) {
      return;
    }
    if (atlasState.personId !== null) {
      setSelectionHistory((current) => [...current, atlasState.personId as string]);
    }
    setFocusDepth('direct');
    setRelationshipFilters(RELATIONSHIP_FILTERS);
    setControlsExpanded(false);
    commit({ personId });
  };
  const openStoredTrace = (trace: StoredTraceReference) => {
    setSelectionHistory([]);
    setControlsExpanded(false);
    setSelectedRouteKey(null);
    setFeaturedGuess(null);
    commit({
      personId: null,
      traceFrom: trace.from,
      traceTo: trace.to,
      storyId: trace.storyId ?? null,
      lenses: trace.lenses ?? DEFAULT_RELATIONSHIP_LENSES,
    });
    if (libraryController !== null) {
      setLibrarySnapshot(libraryController.addRecentTrace(trace));
    }
  };
  const goBack = () => {
    if (selectionHistory.length > 0) {
      window.history.back();
    } else {
      commit({ personId: null });
      setFocusDepth('direct');
    }
  };
  const closePerson = () => {
    setSelectionHistory([]);
    setFocusDepth('direct');
    setRelationshipFilters(RELATIONSHIP_FILTERS);
    commit({ personId: null });
  };
  const focusPathStep = (sourceId: string, targetId: string) => {
    setFocusRequest((current) => ({
      personIds: [sourceId, targetId],
      mode: 'exact',
      nonce: (current?.nonce ?? 0) + 1,
    }));
  };
  const reset = () => {
    commit({
      personId: null,
      traceFrom: null,
      traceTo: null,
      storyId: null,
      lenses: DEFAULT_RELATIONSHIP_LENSES,
      view: 'graph',
    });
    setSelectionHistory([]);
    setFocusDepth('direct');
    setRelationshipFilters(RELATIONSHIP_FILTERS);
    setControlsExpanded(true);
    setSelectedRouteKey(null);
    setFeaturedGuess(null);
    setResetToken((value) => value + 1);
  };
  const copyTrace = async () => {
    if (shareDetails === null) {
      return;
    }
    try {
      await copyText(shareDetails.url);
      setShareFeedback('Trace link copied.');
    } catch {
      setShareFeedback('The trace link could not be copied in this browser.');
    }
  };
  const shareTrace = async () => {
    if (shareDetails === null || typeof navigator.share !== 'function') {
      return;
    }
    try {
      await navigator.share(shareDetails);
      setShareFeedback('Trace shared.');
    } catch (error) {
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        setShareFeedback('The trace could not be shared in this browser.');
      }
    }
  };
  const saveTraceSnapshot = () => {
    if (atlasState.view === 'list') {
      commit({ view: 'graph' });
    }
    setShareFeedback(
      atlasState.view === 'list'
        ? 'Opening Graph view to prepare the high-resolution map.'
        : 'Preparing the high-resolution map.',
    );
    setSnapshotRequestToken((value) => value + 1);
  };
  const createPathCardArtifact = async () => {
    if (
      selectedPath === null ||
      shareDetails === null ||
      fromPerson === null ||
      toPerson === null ||
      index === null
    ) {
      return null;
    }
    return createCompletedPathCard({
      fromName: fromPerson.name,
      toName: toPerson.name,
      stepCount: selectedPath.relationships.length,
      chainNames: selectedPath.people.map(
        (personId) => index.peopleById.get(personId)?.name ?? personId,
      ),
      snapshotDate: manifest.snapshotDate,
      canonicalUrl: shareDetails.url,
    });
  };
  const savePathCard = async () => {
    try {
      const artifact = await createPathCardArtifact();
      if (artifact === null) {
        return;
      }
      setShareFeedback(
        downloadCompletedPathCard(artifact)
          ? 'Story card downloaded.'
          : 'Story card export is unavailable in this browser.',
      );
    } catch {
      setShareFeedback('The story card could not be created in this browser.');
    }
  };
  const chooseTemptation = (temptation: NextTemptation) => {
    if (temptation.path === null) {
      selectPerson(temptation.targetId);
      return;
    }
    const from = temptation.path.people[0];
    const to = temptation.path.people.at(-1);
    if (from === undefined || to === undefined) {
      return;
    }
    setSelectedRouteKey(storyPathKey(temptation.path));
    setControlsExpanded(false);
    commit({ personId: null, traceFrom: from, traceTo: to, storyId: null, view: 'graph' });
  };
  const jumpTrail = (entryId: string) => {
    const target = explorationTrailJumpTarget(trail, entryId);
    if (target === null) {
      return;
    }
    if (target.kind === 'person') {
      selectPerson(target.personId);
      return;
    }
    setSelectedRouteKey(target.pathKey);
    setControlsExpanded(false);
    commit({
      personId: null,
      traceFrom: target.sourceId,
      traceTo: target.targetId,
      storyId: null,
      view: 'graph',
    });
  };
  const toggleLens = (lens: RelationshipLens) => {
    const enabled = new Set(atlasState.lenses);
    if (enabled.has(lens)) {
      if (enabled.size === 1) {
        return;
      }
      enabled.delete(lens);
    } else {
      enabled.add(lens);
    }
    commit({ lenses: RELATIONSHIP_LENSES.filter((candidate) => enabled.has(candidate)) });
  };

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      const target = event.target;
      if (
        event.key !== 'Escape' ||
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        return;
      }
      if (atlasState.personId !== null) {
        event.preventDefault();
        goBack();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  });

  const snapshotTitle =
    displayPath !== null && fromPerson !== null && toPerson !== null
      ? `${fromPerson.name} → ${toPerson.name} · ${displayPath.relationships.length} steps`
      : selectedSearchPerson === undefined
        ? 'Explore source-linked connections'
        : `${selectedSearchPerson.name} · relationship web`;
  const compactContext =
    storyConcealed && fromPerson !== null && toPerson !== null
      ? `${fromPerson.name} to ${toPerson.name} · ready to reveal`
      : displayPath !== null && fromPerson !== null && toPerson !== null
        ? `${fromPerson.name} to ${toPerson.name} · ${displayPath.relationships.length} ${
            displayPath.relationships.length === 1 ? 'step' : 'steps'
          }`
        : (selectedSearchPerson?.name ??
          fromPerson?.name ??
          toPerson?.name ??
          'Active constellation exploration');

  const visibleRelationshipCount =
    visibleDataset?.relationships.length ??
    atlasState.lenses.reduce((total, lens) => total + lensCounts[lens], 0);
  const progressiveMessage =
    graphState.status === 'error'
      ? `${graphState.message} Search and starter discovery remain available.`
      : atlasState.traceFrom !== null && atlasState.traceTo !== null
        ? 'Your people are selected. Loading the complete constellation to document their path…'
        : atlasState.personId !== null
          ? 'Your person is selected. Loading the complete constellation and sourced details…'
          : 'Search is ready. Loading the complete relationship constellation…';

  return (
    <div className="app-shell">
      <SiteHeader
        badge={`Data snapshot ${manifest.snapshotDate}`}
        badgeDescription="Dated dataset snapshot; the atlas is not updated live."
      />

      <main
        id="main-content"
        className="atlas"
        data-trace-active={atlasState.traceFrom !== null || atlasState.traceTo !== null}
      >
        <section
          className="atlas__controls"
          aria-label="Constellation controls"
          data-compact={intentActive && !controlsExpanded}
          data-person-focused={atlasState.personId !== null || undefined}
        >
          {intentActive && !controlsExpanded ? (
            <div className="atlas__compact-command">
              <div>
                <p className="eyebrow">Current exploration</p>
                <p>{compactContext}</p>
              </div>
              <button
                className="button button--gold button--small"
                type="button"
                aria-expanded="false"
                onClick={() => setControlsExpanded(true)}
              >
                Search or connect
              </button>
            </div>
          ) : null}
          <div className="atlas__intro">
            <p className="eyebrow">Constellation explorer</p>
            <h1 className="sr-only">Entangled Vega public-figure connection atlas</h1>
            <p className="atlas__tagline">
              <strong>Every star has a story.</strong> Follow the lines.
            </p>
          </div>
          <div className="atlas__search-row">
            <AtlasSearchControls
              key={resetToken}
              people={activePeople}
              starterPeople={starterPeople}
              from={fromPerson}
              to={toPerson}
              onExplore={selectPerson}
              onFrom={(personId) => {
                if (atlasState.traceTo !== null) {
                  setControlsExpanded(false);
                }
                commit({ personId: null, traceFrom: personId });
              }}
              onTo={(personId) => {
                if (atlasState.traceFrom !== null) {
                  setControlsExpanded(false);
                }
                commit({ personId: null, traceTo: personId });
              }}
              onSwap={() =>
                commit({
                  traceFrom: atlasState.traceTo,
                  traceTo: atlasState.traceFrom,
                })
              }
              onSurprise={suggestedTraces.length === 0 ? undefined : surprise}
            />
            <LensControl counts={lensCounts} enabled={atlasState.lenses} onToggle={toggleLens} />
            <div className="segmented" aria-label="Choose graph or list view">
              <button
                type="button"
                aria-pressed={atlasState.view === 'graph'}
                onClick={() => commit({ view: 'graph' })}
              >
                Graph
              </button>
              <button
                type="button"
                aria-pressed={atlasState.view === 'list'}
                onClick={() => commit({ view: 'list' })}
              >
                List
              </button>
            </div>
            <button className="button" type="button" onClick={reset}>
              Reset
            </button>
            {intentActive ? (
              <button
                className="button atlas__recede"
                type="button"
                aria-expanded="true"
                onClick={() => setControlsExpanded(false)}
              >
                {atlasState.view === 'graph' ? 'Give graph more room' : 'Give list more room'}
              </button>
            ) : null}
          </div>
          <div className="atlas__continuity" aria-label="Daily and saved discovery">
            <a className="button button--small" href="/challenge/">
              Try Daily connection
            </a>
            {librarySnapshot === null || libraryController === null ? null : (
              <SavedLibrary
                snapshot={librarySnapshot}
                peopleById={activePeopleById}
                onOpenPerson={selectPerson}
                onOpenTrace={openStoredTrace}
                onRemoveFavoritePerson={(personId) =>
                  setLibrarySnapshot(libraryController.removeFavoritePerson(personId))
                }
                onRemoveFavoriteTrace={(trace) =>
                  setLibrarySnapshot(libraryController.removeFavoriteTrace(trace))
                }
                onRemoveRecentPerson={(personId) =>
                  setLibrarySnapshot(libraryController.removeRecentPerson(personId))
                }
                onRemoveRecentTrace={(trace) =>
                  setLibrarySnapshot(libraryController.removeRecentTrace(trace))
                }
                onClearRecents={() => setLibrarySnapshot(libraryController.clearRecents())}
                onClearAll={() => setLibrarySnapshot(libraryController.clearAll())}
              />
            )}
          </div>
          <p className="sr-only" role="status" aria-live="polite">
            Enabled relationship lenses:{' '}
            {atlasState.lenses
              .map((lens) => (lens === 'personal' ? 'Personal' : 'Family'))
              .join(' and ')}
            . {visibleRelationshipCount.toLocaleString('en-US')} source-linked connections
            available.
          </p>
          {visibleUrlIssues.length === 0 ? null : (
            <p className="notice" role="status">
              This link contains unavailable state for: {invalidLinkDescription(visibleUrlIssues)}.
              The valid parts were kept.
            </p>
          )}
          {graphicsNotice ? (
            <p className="notice" role="status">
              WebGL is unavailable, so the equivalent list view is active.
            </p>
          ) : null}
          {atlasState.personId === null &&
          atlasState.traceFrom === null &&
          atlasState.traceTo === null &&
          index !== null ? (
            <FeaturedDiscoveries
              traces={suggestedTraces}
              index={index}
              onChoose={(source, target, storyId) => {
                setControlsExpanded(false);
                setSelectedRouteKey(null);
                setFeaturedGuess(null);
                commit({
                  personId: null,
                  traceFrom: source,
                  traceTo: target,
                  storyId,
                  view: 'graph',
                });
              }}
            />
          ) : null}
          {index === null ? null : (
            <TraceBanner
              from={atlasState.traceFrom}
              to={atlasState.traceTo}
              path={selectedPath}
              primaryPath={path}
              alternatives={alternativePaths}
              recovery={noPathRecovery}
              featured={selectedPathIsFeatured}
              concealed={storyConcealed}
              selectedGuess={activeFeaturedGuess?.guess ?? null}
              bridgePrompt={featuredBridgePrompt}
              selectedBridgeChoiceId={activeFeaturedGuess?.bridgeChoiceId ?? null}
              featuredAnswerSteps={activeStoryPath?.relationships.length ?? null}
              playbackYieldKey={`${atlasState.lenses.join(',')}|${atlasState.view}|${atlasState.personId ?? ''}|${atlasState.traceFrom ?? ''}|${atlasState.traceTo ?? ''}|${storyConcealed}`}
              index={index}
              snapshotDate={manifest.snapshotDate}
              enabledLenses={atlasState.lenses}
              shareFeedback={shareFeedback}
              canShare={typeof navigator.share === 'function'}
              favorite={currentTraceFavorite}
              onClear={() => {
                setSelectedRouteKey(null);
                commit({ traceFrom: null, traceTo: null });
              }}
              onCopy={() => {
                void copyTrace();
              }}
              onShare={() => {
                void shareTrace();
              }}
              onSaveSnapshot={saveTraceSnapshot}
              onToggleFavorite={() => {
                if (libraryController === null || currentTraceReference === null) return;
                setLibrarySnapshot(
                  currentTraceFavorite
                    ? libraryController.removeFavoriteTrace(currentTraceReference)
                    : libraryController.addFavoriteTrace(currentTraceReference),
                );
              }}
              onRetryAllLenses={() => commit({ lenses: DEFAULT_RELATIONSHIP_LENSES })}
              onTryConnection={(from, to) => {
                setControlsExpanded(false);
                commit({ personId: null, traceFrom: from, traceTo: to, storyId: null });
              }}
              onSelectPerson={selectPerson}
              onGuess={(guess) => {
                if (activeStoryKey !== null) {
                  setFeaturedGuess((current) => ({
                    storyKey: activeStoryKey,
                    guess,
                    bridgeChoiceId:
                      current?.storyKey === activeStoryKey ? current.bridgeChoiceId : null,
                    revealed: false,
                  }));
                }
              }}
              onBridgeGuess={(bridgeChoiceId) => {
                if (activeStoryKey !== null) {
                  setFeaturedGuess((current) => ({
                    storyKey: activeStoryKey,
                    guess: current?.storyKey === activeStoryKey ? current.guess : null,
                    bridgeChoiceId,
                    revealed: false,
                  }));
                }
              }}
              onReveal={() => {
                if (activeStoryKey !== null) {
                  setFeaturedGuess((current) => ({
                    storyKey: activeStoryKey,
                    guess: current?.storyKey === activeStoryKey ? current.guess : null,
                    bridgeChoiceId:
                      current?.storyKey === activeStoryKey ? current.bridgeChoiceId : null,
                    revealed: true,
                  }));
                }
              }}
              onChooseRoute={(nextPath) => setSelectedRouteKey(storyPathKey(nextPath))}
              onFocusStep={focusPathStep}
              onPathComplete={(completedPath) => {
                if (fromPerson !== null && toPerson !== null) {
                  dispatchTrail({
                    type: 'complete-path',
                    path: completedPath,
                    sourceLabel: fromPerson.name,
                    targetLabel: toPerson.name,
                  });
                }
                if (libraryController !== null && currentTraceReference !== null) {
                  setLibrarySnapshot(libraryController.addRecentTrace(currentTraceReference));
                }
              }}
              onSaveCard={() => {
                void savePathCard();
              }}
            />
          )}
          {atlasState.personId !== null ? null : (
            <>
              <ExplorationTrail
                entries={trail.entries}
                onJump={jumpTrail}
                onClear={() => dispatchTrail({ type: 'clear' })}
              />
              {temptationPersonId === null ? null : (
                <NextTemptations
                  fromName={temptationPersonName}
                  temptations={nextTemptations}
                  onChoose={chooseTemptation}
                />
              )}
            </>
          )}
        </section>

        <div className="atlas__stage" data-view={atlasState.view}>
          {visibleDataset === null || index === null ? (
            <div className="constellation constellation--loading" role="status" aria-live="polite">
              <div className="data-status">
                <p>{progressiveMessage}</p>
                {graphState.status === 'error' ? (
                  <button className="button button--gold" type="button" onClick={onRetryGraph}>
                    Retry constellation load
                  </button>
                ) : null}
              </div>
            </div>
          ) : atlasState.view === 'graph' ? (
            <Suspense
              fallback={
                <div className="constellation constellation--loading" role="status">
                  Mapping the constellation…
                </div>
              }
            >
              <ConstellationView
                dataset={visibleDataset}
                index={index}
                selectedId={atlasState.personId}
                path={displayPath}
                focusDepth={focusDepth}
                relationshipFilters={relationshipFilters}
                snapshotTitle={snapshotTitle}
                snapshotRequestToken={snapshotRequestToken}
                onSnapshotRequestHandled={() => setSnapshotRequestToken(0)}
                onSnapshotStatus={setShareFeedback}
                focusRequest={focusRequest}
                resetToken={resetToken}
                onSelect={selectPerson}
                onUnavailable={() => {
                  setGraphicsNotice(true);
                  commit({ view: 'list' }, true);
                }}
              />
            </Suspense>
          ) : (
            <ListView
              dataset={visibleDataset}
              index={index}
              selectedId={atlasState.personId}
              path={displayPath}
              focusDepth={focusDepth}
              relationshipFilters={relationshipFilters}
              onSelect={selectPerson}
            />
          )}

          <footer className="atlas__legend">
            <div className="legend" aria-label="Relationship legend" tabIndex={0}>
              <span>
                <i className="legend__line legend__line--spouse" /> Spouse
              </span>
              <span>
                <i className="legend__line legend__line--partner" /> Partner
              </span>
              <span>
                <i className="legend__line legend__line--reported" /> Supplemental{' '}
                <small>Reported, Rumored, or Disputed</small>
              </span>
              <span>
                <i className="legend__line legend__line--family" /> Family
              </span>
              <span>
                <i className="legend__line legend__line--trace" /> Trace
              </span>
            </div>
            <p>
              {(visibleDataset?.people.length ?? activePeople.length).toLocaleString('en-US')}{' '}
              people
              {' · '}
              {visibleRelationshipCount.toLocaleString('en-US')} relationships in enabled lenses ·{' '}
              {visibleDataset?.version ?? manifest.datasetVersion}
            </p>
          </footer>
        </div>

        {selectedPerson === undefined || dataset === null || index === null ? null : (
          <PersonPanel
            person={selectedPerson}
            index={index}
            fixture={dataset.fixture}
            snapshotDate={dataset.snapshotDate}
            limitations={dataset.limitations}
            constellationSummary={selectedSummary}
            suggestions={(nextTemptations.length === 0
              ? index.hubs
              : nextTemptations.map((temptation) => temptation.targetId)
            )
              .map((personId) => index.peopleById.get(personId))
              .filter(
                (person): person is GraphPerson =>
                  person !== undefined && person.id !== selectedPerson.id,
              )
              .slice(0, 4)}
            enabledRelationshipFilters={relationshipFilters}
            focusDepth={focusDepth}
            favorite={selectedPersonFavorite}
            onToggleFavorite={() => {
              if (libraryController === null) return;
              setLibrarySnapshot(
                selectedPersonFavorite
                  ? libraryController.removeFavoritePerson(selectedPerson.id)
                  : libraryController.addFavoritePerson(selectedPerson.id),
              );
            }}
            onClose={closePerson}
            canGoBack={selectionHistory.length > 0}
            onBack={goBack}
            onSelect={selectPerson}
            onConnect={(personId) => {
              setSelectionHistory([]);
              setControlsExpanded(true);
              commit({
                personId: null,
                traceFrom: personId,
                traceTo: null,
                storyId: null,
                view: 'graph',
              });
            }}
            onSetRelationshipFilters={setRelationshipFilters}
            onSetFocusDepth={setFocusDepth}
            workspace={dossierWorkspace}
            onWorkspaceChange={setDossierWorkspace}
            viewMode={atlasState.view}
            onViewModeChange={(view) => commit({ view })}
          />
        )}
      </main>
    </div>
  );
}

export default function App() {
  const [pathname, setPathname] = useState(pagePath);
  useEffect(() => {
    const handlePopState = () => setPathname(pagePath());
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  if (pathname === '/about') {
    return <AboutPage />;
  }
  if (pathname === '/privacy') {
    return <PrivacyPage />;
  }
  if (pathname === '/work-with-us') {
    return <WorkWithUsPage />;
  }
  if (pathname === '/challenge') {
    return <ChallengePage />;
  }
  if (pathname !== '/') {
    return <NotFoundPage />;
  }
  return <AtlasDataBoundary />;
}
